import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { BookOpenText, Lightbulb, Scale, Building2 } from 'lucide-react';
import { Spinner } from '@/components/ui/spinner';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { opinionsApi, type OpinionArticlePublicItem, type OpinionPageType } from '@/services/api';
import { opinionArticlePath } from '@/constants/appRoutes';

const SECTIONS: { pageType: OpinionPageType; title: string; blurb: string; icon: React.ElementType }[] = [
  {
    pageType: 'topic',
    title: 'Topics',
    blurb: 'Consolidated knowledge on RCM subjects and processes.',
    icon: BookOpenText,
  },
  {
    pageType: 'decision',
    title: 'Decisions',
    blurb: 'Supported positions and recommendations, with their rationale.',
    icon: Scale,
  },
  {
    pageType: 'entity',
    title: 'Entities',
    blurb: 'Payers, organizations, and systems worth their own page.',
    icon: Building2,
  },
];

function formatDate(iso?: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString();
}

/**
 * RCM Opinions landing page — browse the LLM-wiki opinion articles
 * (dev/projects/opinion-articles/) grouped by page type.
 */
export function OpinionsListPage() {
  const [articles, setArticles] = useState<OpinionArticlePublicItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await opinionsApi.list({ limit: 200 });
        if (!cancelled) setArticles(response.items);
      } catch {
        if (!cancelled) setError('Failed to load opinion articles.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="mx-auto w-full max-w-4xl px-4 py-8">
      <header className="mb-8">
        <div className="flex items-center gap-2 mb-2">
          <Lightbulb className="h-5 w-5 text-[hsl(var(--primary))]" />
          <h1 className="font-display text-2xl font-semibold text-foreground">RCM Opinions</h1>
        </div>
        <p className="text-sm text-muted-foreground max-w-2xl">
          Curated articles distilled from internal RCM expertise — expert transcripts and Q&A
          sessions, reviewed before publication. Opinion articles complement primary sources and
          are cited in chat with an “RCM Opinion” badge.
        </p>
      </header>

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {loading ? (
        <div className="py-16 flex justify-center">
          <Spinner />
        </div>
      ) : articles.length === 0 && !error ? (
        <p className="py-16 text-center text-sm text-muted-foreground">
          No opinion articles published yet.
        </p>
      ) : (
        <div className="space-y-8">
          {SECTIONS.map(({ pageType, title, blurb, icon: Icon }) => {
            const items = articles.filter((a) => a.page_type === pageType);
            if (items.length === 0) return null;
            return (
              <section key={pageType}>
                <div className="flex items-center gap-2 mb-1">
                  <Icon className="h-4 w-4 text-muted-foreground" />
                  <h2 className="text-lg font-medium text-foreground">{title}</h2>
                  <span className="text-xs text-muted-foreground">({items.length})</span>
                </div>
                <p className="text-xs text-muted-foreground mb-3">{blurb}</p>
                <ul className="grid gap-2 sm:grid-cols-2">
                  {items.map((article) => (
                    <li key={article.id}>
                      <Link
                        to={opinionArticlePath(article.slug)}
                        className="block rounded-lg border bg-card px-4 py-3 shadow-sm transition-colors hover:bg-accent/50 no-underline"
                      >
                        <div className="text-sm font-medium text-foreground leading-snug">
                          {article.title}
                        </div>
                        {article.updated_at && (
                          <div className="mt-1 text-[11px] text-muted-foreground">
                            Updated {formatDate(article.updated_at)}
                          </div>
                        )}
                      </Link>
                    </li>
                  ))}
                </ul>
              </section>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default OpinionsListPage;
